import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { Search, Filter, Download, X } from 'lucide-react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { DataTable, Pagination } from '../components/ui/DataTable'
import { useClusters, useClusterTypes, useOrganisms } from '../api/hooks'
import { useTranslation } from '../hooks/useTranslation'
import { exportClusterList } from '../utils/exportData'
import type { Cluster } from '../types'

/**
 * クラスターブラウザページ
 * 日本語対応済み
 */
export function ClusterBrowserPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [selectedOrganism, setSelectedOrganism] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const limit = 20

  const { data: clusterTypes } = useClusterTypes()
  const { data: organisms } = useOrganisms()
  const { data, isLoading } = useClusters({
    page,
    limit,
    search: search || undefined,
    cluster_type: selectedType || undefined,
    organism: selectedOrganism || undefined,
  })

  const columns = useMemo<ColumnDef<Cluster>[]>(
    () => [
      {
        accessorKey: 'mibig_accession',
        header: t('cluster.mibigAccession'),
        cell: ({ row }) => (
          <span className="font-mono text-accent-cyan">{row.original.mibig_accession}</span>
        ),
      },
      {
        accessorKey: 'organism_name',
        header: t('cluster.organismName'),
        cell: ({ row }) => (
          <span className="italic">{row.original.organism_name}</span>
        ),
      },
      {
        accessorKey: 'biosyn_class',
        header: t('cluster.biosynClass'),
        cell: ({ row }) => {
          const classes = row.original.biosyn_class || []
          return (
            <div className="flex flex-wrap gap-1">
              {classes.map((cls, i) => {
                const variant =
                  cls?.includes('NRPS') ? 'purple' :
                  cls?.includes('PKS') ? 'cyan' :
                  cls?.includes('RiPP') ? 'green' :
                  cls?.includes('Terpene') ? 'amber' : 'default'
                return <Badge key={i} variant={variant}>{cls}</Badge>
              })}
            </div>
          )
        },
      },
      {
        accessorKey: 'gene_count',
        header: t('cluster.geneCount'),
        cell: ({ row }) => (
          <span className="text-gray-400">{row.original.gene_count || 0}</span>
        ),
      },
      {
        accessorKey: 'compounds',
        header: t('cluster.compounds'),
        cell: ({ row }) => (
          <span className="text-gray-400">{row.original.compounds?.length || 0}</span>
        ),
      },
    ],
    [t]
  )

  const totalPages = data ? Math.ceil(data.total / limit) : 1

  const handleRowClick = (cluster: Cluster) => {
    navigate(`/clusters/${cluster.mibig_accession}`)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setSelectedType('')
    setSelectedOrganism('')
    setPage(1)
  }

  const hasActiveFilters = search || selectedType || selectedOrganism

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">{t('features.clusterBrowser.title')}</h1>
          <p className="text-gray-400 mt-1">
            {data?.total?.toLocaleString() || '...'} {t('stats.clusters')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showFilters ? 'primary' : 'secondary'}
            leftIcon={<Filter className="w-4 h-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {t('cluster.filterBy')}
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={() => data?.items && exportClusterList(data.items)}
            disabled={!data?.items || data.items.length === 0}
          >
            {t('common.download')}
          </Button>
        </div>
      </div>

      {/* 検索バー */}
      <form onSubmit={handleSearch}>
        <Input
          placeholder={t('cluster.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search className="w-5 h-5" />}
          rightIcon={
            search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )
          }
        />
      </form>

      {/* フィルターパネル */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <Card variant="solid">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('cluster.biosynClass')}
                  </label>
                  <select
                    value={selectedType}
                    onChange={(e) => {
                      setSelectedType(e.target.value)
                      setPage(1)
                    }}
                    className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-accent-cyan/50"
                  >
                    <option value="" style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>{t('cluster.allClasses')}</option>
                    {clusterTypes?.map((type) => (
                      <option key={type} value={type} style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('cluster.organism')}
                  </label>
                  <select
                    value={selectedOrganism}
                    onChange={(e) => {
                      setSelectedOrganism(e.target.value)
                      setPage(1)
                    }}
                    className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-accent-cyan/50"
                  >
                    <option value="" style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>{t('cluster.allOrganisms')}</option>
                    {organisms?.filter(org => org && org.trim() !== '').map((org) => (
                      <option key={org} value={org} style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>
                        {org}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      leftIcon={<X className="w-4 h-4" />}
                      onClick={clearFilters}
                    >
                      {t('common.reset')}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* アクティブフィルタータグ */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {search && (
            <Badge variant="cyan">
              {t('common.search')}: "{search}"
              <button onClick={() => setSearch('')} className="ml-2 hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {selectedType && (
            <Badge variant="purple">
              {t('cluster.biosynClass')}: {selectedType}
              <button onClick={() => setSelectedType('')} className="ml-2 hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {selectedOrganism && (
            <Badge variant="green">
              {t('cluster.organism')}: {selectedOrganism}
              <button onClick={() => setSelectedOrganism('')} className="ml-2 hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {/* Data Table */}
      <DataTable
        data={data?.items || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={handleRowClick}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  )
}
